// LeaveRequestForm.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const LeaveRequestForm = ({ show, onHide, onLeaveRequestAdded }) => {
    const [formData, setFormData] = useState({
        emp_code: '',
        emp_id: '',
        leave_type_id: '',
        from_date: '',
        to_date: '',
        total_days: 0,
        first_day_half: false,
        first_day_period: 'Morning',
        last_day_half: false,
        last_day_period: 'Morning',
        reason: ''
    });

    const [leaveTypes, setLeaveTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showEmployeeList, setShowEmployeeList] = useState(false);
    const [today] = useState(new Date().toISOString().split('T')[0]);

    const API_BASE = process.env.REACT_APP_API_BASE;

    const fetchLeaveTypes = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}leave/get_leave_types.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLeaveTypes(response.data.leave_types || []);
            }
        } catch (error) {
            console.error('Error fetching leave types:', error);
        }
    }, [API_BASE]);

    const searchEmployees = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_employees.php?search=${searchTerm}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setFilteredEmployees(response.data.employees);
                setShowEmployeeList(true);
            }
        } catch (error) {
            console.error('Error searching employees:', error);
        }
    }, [API_BASE, searchTerm]);

    const calculateTotalDays = useCallback(() => {
        const { from_date, to_date, first_day_half, last_day_half } = formData;

        if (!from_date || !to_date) {
            setFormData(prev => ({ ...prev, total_days: 0 }));
            return;
        }

        const from = new Date(from_date);
        const to = new Date(to_date);

        if (from > to) {
            setFormData(prev => ({ ...prev, total_days: 0 }));
            return;
        }

        const timeDiff = to.getTime() - from.getTime();
        const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;

        console.log(`From: ${from_date}, To: ${to_date}, Days diff: ${dayDiff}`);

        let totalDays = dayDiff;

        if (first_day_half) {
            totalDays -= 0.5;
            console.log('First day half: -0.5');
        }
        if (last_day_half) {
            totalDays -= 0.5;
            console.log('Last day half: -0.5');
        }

        if (dayDiff === 1 && first_day_half && last_day_half) {
            totalDays = 0;
        } else if (totalDays < 0) {
            totalDays = 0;
        }

        console.log(`Final total days: ${totalDays}`);

        setFormData(prev => ({
            ...prev,
            total_days: parseFloat(totalDays.toFixed(1))
        }));
    }, [formData]);

    useEffect(() => {
        if (show) {
            fetchLeaveTypes();
            resetForm();
        }
    }, [show, fetchLeaveTypes]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm.length >= 2) {
                searchEmployees();
            } else {
                setFilteredEmployees([]);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, searchEmployees]);

    useEffect(() => {
        calculateTotalDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.from_date, formData.to_date, formData.first_day_half, formData.last_day_half]);

    const handleEmployeeSelect = (employee) => {
        setFormData(prev => ({
            ...prev,
            emp_code: employee.emp_code,
            emp_id: employee.emp_id
        }));
        setSearchTerm(`${employee.first_name} ${employee.last_name} (${employee.emp_code})`);
        setShowEmployeeList(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;

        // If setting to_date, make sure it's not before from_date
        if (name === 'to_date' && formData.from_date && value < formData.from_date) {
            setError('To date cannot be before from date');
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (error && error.includes('date cannot be')) {
            setError('');
        }
    };



    const validateForm = () => {
        const errors = [];

        if (!formData.emp_id) errors.push('Please select an employee');
        if (!formData.leave_type_id) errors.push('Leave type is required');
        if (!formData.from_date) errors.push('From date is required');
        if (!formData.to_date) errors.push('To date is required');
        if (!formData.reason.trim()) errors.push('Reason is required');

        if (formData.from_date && formData.to_date) {
            const from = new Date(formData.from_date);
            const to = new Date(formData.to_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (from > to) errors.push('From date cannot be after to date');
            if (from < today) errors.push('From date cannot be in the past');
            if (formData.total_days <= 0) errors.push('Total days must be greater than 0');

            // Check if it's same day with both half days (invalid)
            if (formData.from_date === formData.to_date && formData.first_day_half && formData.last_day_half) {
                errors.push('Cannot have both half days on a single day');
            }
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();
        if (errors.length > 0) {
            setError(errors.join(', '));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('authToken');
            const leaveRequestData = {
                ...formData,
                first_day_half: formData.first_day_half ? 1 : 0,
                last_day_half: formData.last_day_half ? 1 : 0,
                total_days: formData.total_days // Use the calculated value
            };

            console.log('Submitting leave request:', leaveRequestData);

            const response = await axios.post(
                `${API_BASE}leave/add_leave_request.php`,
                leaveRequestData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                alert('Leave request submitted successfully!');
                onLeaveRequestAdded();
                onHide();
                resetForm();
            } else {
                setError(response.data.error || 'Failed to submit leave request');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to submit leave request';
            setError(errorMsg);
            console.error('Error submitting leave request:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            emp_code: '',
            emp_id: '',
            leave_type_id: '',
            from_date: '',
            to_date: '',
            total_days: 0,
            first_day_half: false,
            first_day_period: 'Morning',
            last_day_half: false,
            last_day_period: 'Morning',
            reason: ''
        });
        setSearchTerm('');
        setError('');
        setShowEmployeeList(false);
    };

    const handleCancel = () => {
        resetForm();
        onHide();
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header" style={{
                        background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                        borderBottom: 'none'
                    }}>
                        <h5 className="modal-title text-white">
                            <i className="bi bi-calendar-plus me-2"></i>
                            New Leave Request
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleCancel}
                            disabled={loading}
                        ></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <span>{error}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setError('')}
                                    ></button>
                                </div>
                            )}

                            {/* Employee Search - Autocomplete */}
                            <div className="mb-3">
                                <label htmlFor="employeeSearch" className="form-label fw-medium">
                                    Employee <span className="text-danger">*</span>
                                </label>
                                <div className="position-relative">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="employeeSearch"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            if (!e.target.value) {
                                                setFormData(prev => ({ ...prev, emp_code: '', emp_id: '' }));
                                                setShowEmployeeList(false);
                                            }
                                        }}
                                        placeholder="Type to search by name or employee code..."
                                        required
                                        disabled={loading}
                                        autoComplete="off"
                                    />
                                    {showEmployeeList && filteredEmployees.length > 0 && (
                                        <div className="position-absolute top-100 start-0 end-0 z-3 mt-1" style={{
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            backgroundColor: 'white',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
                                        }}>
                                            {filteredEmployees.map(employee => (
                                                <div
                                                    key={employee.emp_id}
                                                    className="p-2 border-bottom"
                                                    style={{
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                    onClick={() => handleEmployeeSelect(employee)}
                                                >
                                                    <div className="fw-medium">
                                                        {employee.first_name} {employee.last_name}
                                                    </div>
                                                    <small className="text-muted d-block">
                                                        Code: {employee.emp_code} | Email: {employee.email}
                                                    </small>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {formData.emp_id && (
                                    <div className="form-text text-success mt-1">
                                        <i className="bi bi-check-circle-fill me-1"></i>
                                        Selected: {searchTerm}
                                    </div>
                                )}
                            </div>

                            {/* Leave Type */}
                            <div className="mb-3">
                                <label htmlFor="leave_type_id" className="form-label fw-medium">
                                    Leave Type <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select"
                                    id="leave_type_id"
                                    name="leave_type_id"
                                    value={formData.leave_type_id}
                                    onChange={handleChange}
                                    required
                                    disabled={loading || leaveTypes.length === 0}
                                >
                                    <option value="">{leaveTypes.length === 0 ? 'Loading leave types...' : 'Select leave type'}</option>
                                    {leaveTypes.map(leaveType => (
                                        <option key={leaveType.leave_type_id} value={leaveType.leave_type_id}>
                                            {leaveType.leave_name} ({leaveType.leave_code})
                                        </option>
                                    ))}
                                </select>
                                {leaveTypes.length === 0 && (
                                    <div className="form-text text-warning">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        No leave types available. Please create leave types first.
                                    </div>
                                )}
                            </div>

                            {/* Date Range */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label htmlFor="from_date" className="form-label fw-medium">
                                        From Date <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="from_date"
                                        name="from_date"
                                        value={formData.from_date}
                                        onChange={handleDateChange}
                                        min={today}
                                        required
                                        disabled={loading}
                                    />
                                    <div className="form-text">
                                        Cannot be earlier than today ({new Date(today).toLocaleDateString()})
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label htmlFor="to_date" className="form-label fw-medium">
                                        To Date <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="to_date"
                                        name="to_date"
                                        value={formData.to_date}
                                        onChange={handleDateChange}
                                        min={formData.from_date || today}
                                        required
                                        disabled={loading}
                                    />
                                    <div className="form-text">
                                        {formData.from_date ? `Earliest: ${formatDate(formData.from_date)}` : 'Select from date first'}
                                    </div>
                                </div>
                            </div>

                            {/* Half Day Options */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="form-check mb-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="first_day_half"
                                                    name="first_day_half"
                                                    checked={formData.first_day_half}
                                                    onChange={handleChange}
                                                    disabled={loading}
                                                />
                                                <label className="form-check-label fw-medium" htmlFor="first_day_half">
                                                    First Day Half Day
                                                </label>
                                            </div>
                                            {formData.first_day_half && (
                                                <div className="mt-2">
                                                    <label className="form-label small">Select Period:</label>
                                                    <div className="btn-group w-100" role="group">
                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="first_day_period"
                                                            id="first_morning"
                                                            value="Morning"
                                                            checked={formData.first_day_period === 'Morning'}
                                                            onChange={handleChange}
                                                            disabled={loading}
                                                        />
                                                        <label className="btn btn-outline-primary btn-sm" htmlFor="first_morning">
                                                            Morning
                                                        </label>
                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="first_day_period"
                                                            id="first_afternoon"
                                                            value="Afternoon"
                                                            checked={formData.first_day_period === 'Afternoon'}
                                                            onChange={handleChange}
                                                            disabled={loading}
                                                        />
                                                        <label className="btn btn-outline-primary btn-sm" htmlFor="first_afternoon">
                                                            Afternoon
                                                        </label>
                                                    </div>
                                                    <div className="form-text small">
                                                        Morning: 9 AM - 1 PM | Afternoon: 1 PM - 5 PM
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="form-check mb-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="last_day_half"
                                                    name="last_day_half"
                                                    checked={formData.last_day_half}
                                                    onChange={handleChange}
                                                    disabled={loading}
                                                />
                                                <label className="form-check-label fw-medium" htmlFor="last_day_half">
                                                    Last Day Half Day
                                                </label>
                                            </div>
                                            {formData.last_day_half && (
                                                <div className="mt-2">
                                                    <label className="form-label small">Select Period:</label>
                                                    <div className="btn-group w-100" role="group">
                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="last_day_period"
                                                            id="last_morning"
                                                            value="Morning"
                                                            checked={formData.last_day_period === 'Morning'}
                                                            onChange={handleChange}
                                                            disabled={loading}
                                                        />
                                                        <label className="btn btn-outline-info btn-sm" htmlFor="last_morning">
                                                            Morning
                                                        </label>
                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="last_day_period"
                                                            id="last_afternoon"
                                                            value="Afternoon"
                                                            checked={formData.last_day_period === 'Afternoon'}
                                                            onChange={handleChange}
                                                            disabled={loading}
                                                        />
                                                        <label className="btn btn-outline-info btn-sm" htmlFor="last_afternoon">
                                                            Afternoon
                                                        </label>
                                                    </div>
                                                    <div className="form-text small">
                                                        Morning: 9 AM - 1 PM | Afternoon: 1 PM - 5 PM
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Total Days Display */}
                            <div className="mb-3">
                                <div className="card bg-light">
                                    <div className="card-body">
                                        <div className="row align-items-center">
                                            <div className="col-md-8">
                                                <label className="form-label fw-medium mb-0">Total Leave Duration</label>
                                                <div className="form-text">
                                                    {formData.from_date && formData.to_date ? (
                                                        <div>
                                                            <div>
                                                                <strong>Period:</strong> {formatDate(formData.from_date)} to {formatDate(formData.to_date)}
                                                            </div>
                                                            <div className="mt-1">
                                                                {formData.first_day_half && (
                                                                    <span className="badge bg-primary me-2">
                                                                        First Day: {formData.first_day_period} half
                                                                    </span>
                                                                )}
                                                                {formData.last_day_half && (
                                                                    <span className="badge bg-info">
                                                                        Last Day: {formData.last_day_period} half
                                                                    </span>
                                                                )}
                                                                {!formData.first_day_half && !formData.last_day_half && (
                                                                    <span className="badge bg-success">Full Days Only</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : 'Select dates to calculate duration'}
                                                </div>
                                            </div>
                                            <div className="col-md-4 text-end">
                                                <div className="display-5 fw-bold text-primary">
                                                    {formData.total_days.toFixed(1)}
                                                </div>
                                                <div className="text-muted small">Days</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Reason */}
                            <div className="mb-3">
                                <label htmlFor="reason" className="form-label fw-medium">
                                    Reason for Leave <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    id="reason"
                                    name="reason"
                                    rows="4"
                                    value={formData.reason}
                                    onChange={handleChange}
                                    placeholder="Please provide a detailed reason for your leave request..."
                                    required
                                    disabled={loading}
                                    style={{ resize: 'vertical' }}
                                ></textarea>
                                <div className="form-text">
                                    Be specific about the purpose of your leave
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer" style={{
                            background: 'linear-gradient(135deg, rgba(0, 123, 255, 0.05) 0%, rgba(0, 86, 179, 0.05) 100%)',
                            borderTop: '1px solid rgba(0, 123, 255, 0.1)'
                        }}>
                            <button
                                type="button"
                                className="btn btn-light border-secondary-subtle"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                <i className="bi bi-x-circle me-2"></i>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn text-white border-0 shadow-sm"
                                disabled={loading}
                                style={{
                                    background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                                    fontWeight: '500',
                                    letterSpacing: '0.5px',
                                    transition: 'all 0.3s',
                                    padding: '0.5rem 1.5rem'
                                }}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-send me-2"></i>
                                        Submit Leave Request
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LeaveRequestForm;